import { useCallback, useMemo, useRef } from "react";
import type {
  GridApi,
  ICellRendererParams,
  RowClassParams,
  ValueFormatterParams,
} from "ag-grid-community";
import type { SimpleTableColumn } from "@promptlayer/ui";

import { StatusCellBadge } from "../SpanDetails/StatusCellRenderer";
import {
  formatDurationLabel,
  formatSpanTypeLabel,
  getSpanDisplayName,
  getSpanDisplayType,
} from "../SpanDetails/utils";
import { SpanStatus } from "../utils/calculateSpanStatus";

import { SpanNode } from "@/types/spans";

export interface SpanTableRow {
  span_id: string;
  id: string;
  orgHierarchy: string[];
  name: string;
  status: "Error" | "Running" | "Success" | "Partially Failed" | "Failed";
  type: string;
  duration: number;
  start: string;
  end: string | null;
  status_code?: string;
  events?: { name: string }[];
  attributes?: Record<string, unknown>;
  parent_id: string | null;
  request_log_id: number | null;
  children: SpanNode[];
  iteration?: number;
  [key: string]: unknown;
}

interface UseTraceTableProps {
  spanTree: SpanNode[] | null;
  timeRange: [number, number];
  traceStartTime: number;
  traceDuration: number;
}

export const useTraceTable = ({
  spanTree,
  timeRange,
  traceStartTime,
  traceDuration,
}: UseTraceTableProps) => {
  const gridApiRef = useRef<GridApi<SpanTableRow> | null>(null);

  const setGridApi = useCallback((api: GridApi<SpanTableRow>) => {
    gridApiRef.current = api;
  }, []);

  const getGridApi = useCallback(() => gridApiRef.current, [gridApiRef]);

  const spanTableData = useMemo(() => {
    if (!spanTree) {
      return [];
    }

    const convertToTableData = (
      nodes: SpanNode[],
      parentPath: string[] = [],
      isParentLoop = false,
    ): SpanTableRow[] => {
      const result: SpanTableRow[] = [];

      nodes.forEach((node, index) => {
        const hasError =
          node.status?.status_code === "StatusCode.ERROR" ||
          node.events?.some(event => event.name === "exception");
        const nodeType = getSpanDisplayType(node) || "Unknown";
        const duration = node.end
          ? new Date(node.end).getTime() - new Date(node.start).getTime()
          : Date.now() - new Date(node.start).getTime();

        let status: "Error" | "Running" | "Success" | "Partially Failed" | "Failed";
        if (hasError) {
          status = "Error";
        } else if (node.end === null) {
          status = "Running";
        } else {
          status = "Success";
        }

        const currentPath = [...parentPath, node.span_id];
        const isLoop = nodeType === "WHILE_LOOP" || nodeType === "FOR_LOOP";

        const row: SpanTableRow = {
          ...node,
          id: node.span_id,
          orgHierarchy: currentPath,
          name: getSpanDisplayName(node),
          status,
          type: nodeType,
          duration,
          iteration: isParentLoop ? index : undefined,
        };

        if (node.children.length > 0) {
          const childRows = convertToTableData(node.children, currentPath, isLoop);
          result.push(...childRows);

          const directChildIds = new Set(node.children.map(child => child.span_id));
          const directChildRows = childRows.filter(childRow =>
            directChildIds.has(childRow.span_id),
          );

          if (directChildRows.length > 0 && status !== "Running") {
            const childStatuses = directChildRows.map(child => child.status);
            const hasErrorChildren = childStatuses.some(s => s === "Error");
            const hasSuccessfulChildren = childStatuses.some(s => s === "Success");
            const allChildrenError = childStatuses.every(s => s === "Error" || s === "Failed");
            const allChildrenSuccessful = childStatuses.every(s => s === "Success");

            if (allChildrenError) {
              row.status = "Failed";
            } else if (hasErrorChildren && hasSuccessfulChildren) {
              row.status = "Partially Failed";
            } else if (allChildrenSuccessful && !hasError) {
              row.status = "Success";
            } else if (childStatuses.some(s => s === "Partially Failed")) {
              row.status = "Partially Failed";
            }
          }
        }

        result.push(row);
      });

      return result;
    };

    return convertToTableData(spanTree);
  }, [spanTree]);

  const filteredTableData = useMemo(() => {
    if (timeRange[0] === 0 && timeRange[1] === 100) {
      return spanTableData;
    }

    const [startPercent, endPercent] = timeRange;
    const rangeStart = traceStartTime + (traceDuration * startPercent) / 100;
    const rangeEnd = traceStartTime + (traceDuration * endPercent) / 100;

    const spanMap = new Map<string, SpanTableRow>();
    spanTableData.forEach(row => {
      spanMap.set(row.span_id, row);
    });

    const visibleSpanIds = new Set<string>();
    spanTableData.forEach(row => {
      const spanStart = new Date(row.start).getTime();
      const spanEnd = row.end ? new Date(row.end).getTime() : Date.now();
      if (spanStart < rangeEnd && spanEnd > rangeStart) {
        visibleSpanIds.add(row.span_id);
      }
    });

    const markParentsVisible = (spanId: string) => {
      const row = spanMap.get(spanId);
      if (!row || !row.parent_id) {
        return;
      }
      if (!visibleSpanIds.has(row.parent_id)) {
        visibleSpanIds.add(row.parent_id);
        markParentsVisible(row.parent_id);
      }
    };

    Array.from(visibleSpanIds).forEach(spanId => {
      markParentsVisible(spanId);
    });

    return spanTableData.filter(row => visibleSpanIds.has(row.span_id));
  }, [spanTableData, timeRange, traceStartTime, traceDuration]);

  const columns = useMemo<SimpleTableColumn<SpanTableRow>[]>(
    () => [
      {
        header: "Status",
        field: "status",
        width: 145,
        flex: 1,
        cellClass: "ag-cell-left-align",
        cellRenderer: ({ data }: ICellRendererParams<SpanTableRow>) => (
          <StatusCellBadge status={data?.status as SpanStatus} />
        ),
      },
      {
        header: "Type",
        field: "type",
        flex: 1,
        cellClass: "ag-cell-left-align mx-2",
        valueFormatter: (params: ValueFormatterParams<SpanTableRow>): string =>
          params.value ? formatSpanTypeLabel(params.value) : params.value,
      },
      {
        header: "Duration",
        field: "duration",
        flex: 1,
        cellClass: "ag-cell-left-align mx-2",
        sortable: true,
        resizable: false,
        valueFormatter: (params: ValueFormatterParams<SpanTableRow>): string =>
          params.data?.status === "Running"
            ? "-"
            : formatDurationLabel((params.value as number) || 0),
      },
    ],
    [],
  );

  const gridOptions = useMemo(
    () => ({
      groupDefaultExpanded: -1,
      getRowClass: (params: RowClassParams<SpanTableRow>): string | string[] => {
        const row = params.data;
        if (!row) {
          return "";
        }
        const classes: string[] = [];
        if (row.status === "Error") {
          classes.push("bg-red-50/50");
        }
        return classes.join(" ");
      },
    }),
    [],
  );

  return {
    setGridApi,
    getGridApi,
    filteredTableData,
    columns,
    gridOptions,
  };
};
